import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { AuthService } from '@klickdata/core/auth';
import { Customer } from '@klickdata/core/customer';
import { AppScope, ResourceService } from '@klickdata/core/resource';
import { Observable, Subject } from 'rxjs';
import { filter, shareReplay, takeUntil } from 'rxjs/operators';
import { UsersSelectAddEnrollmentComponent } from '../../selection-handler/users-select/users-select-add-enrollment/users-select-add-enrollment.component';
import { Filter } from '@klickdata/core/table';

@Component({
    selector: 'app-resource-enrollment-sheet',
    templateUrl: './resource-enrollment-sheet.component.html',
    styleUrls: ['./resource-enrollment-sheet.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResourceEnrollmentSheetComponent implements OnInit, OnDestroy {
    public assignForm: FormGroup;
    public destroy: Subject<boolean> = new Subject<boolean>();
    public saving: boolean;
    public publicAccess: Observable<boolean>;
    public customer$: Observable<Customer>;
    @ViewChild('usersSelect') usersSelect: UsersSelectAddEnrollmentComponent;
    AppScope = AppScope;
    selectionCount = 0;
    approvalStatus = new FormControl('pending');
    filtersToBeApplied: Filter<string | number>[] = [];
    constructor(
        public bottomSheetRef: MatBottomSheetRef<ResourceEnrollmentSheetComponent>,
        @Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
        protected fb: FormBuilder,
        protected auth: AuthService,
        protected cd: ChangeDetectorRef,
        protected resourceService: ResourceService
    ) {
        this.assignForm = this.fb.group({
            id: [],
            sync_all_users: [],
            users_detach: [[]],
            users_attach: [[]],
        });
        this.customer$ = this.auth.getCustomer().pipe(shareReplay());
    }

    ngOnInit() {
        this.assignForm.patchValue({ id: this.data.occId ?? this.data.resource.id });
        if (this.data.user?.id) {
            this.filtersToBeApplied.push(new Filter('query', [this.data.user.id]));
        }
        this.assignForm.valueChanges
            .pipe(
                takeUntil(this.destroy),
                filter(() => this.remainingSeats && this.remainingSeats > 0)
            )
            .subscribe((value) => {
                this.selectionCount =
                    value.users_attach.filter((id: number) => !value.users_detach.includes(id))?.length ?? 0;
                if (this.remainingSeats - this.selectionCount <= 0) {
                    this.usersSelect.onCloseSelection.next(true);
                    this.usersSelect.notifyOnCloseSelection();
                } else {
                    this.usersSelect.onCloseSelection.next(false);
                }

                this.cd.markForCheck();
            });
    }
    get remainingSeats() {
        // From resource
        if (this.data.resource && !this.data.occId) {
            const totalSeats = this.data.resource.user_collaboration.enroll_approval_required?.metadata?.total_seats;
            if (!totalSeats) {
                return null;
            }
            const approvedCount = this.data.resource.enrollment_counts?.approved;
            return totalSeats - approvedCount;
        }
        // From occasion
        const totalSeats = this.data.resource.user_collaboration.enroll_approval_required.metadata.occasion_dates.find(
            (date) => date.id === this.data.occId
        ).total_seats;
        if (!totalSeats) {
            return null;
        }
        const approvedCount =
            this.data.resource.user_collaboration.enroll_approval_required.metadata.occasion_dates.find(
                (date) => date.id === this.data.occId
            ).approved_count;
        return totalSeats - approvedCount;
    }
    public submit() {
        if (this.assignForm.value.users_attach?.length || this.assignForm.value.sync_all_users !== null) {
            const data = this.assignForm.value;
            if (this.data.customer_id) {
                data.customer_id = this.data.customer_id;
            }

            this.saving = true;
            this.resourceService
                .enrollForUsers(
                    this.data.resource.id,
                    this.assignForm.value.users_attach.filter(
                        (id: number) => !this.assignForm.value.users_detach.includes(id)
                    ),
                    {
                        selectedOccasionId: this.data.occId,
                        permission_type:
                            this.approvalStatus.value === 'pending' || this.approvalStatus.value === 'waiting'
                                ? 'enroll_requested'
                                : 'enroll_approved',
                        permission_value:
                            this.approvalStatus.value === 'pending'
                                ? 1
                                : this.approvalStatus.value === 'approved'
                                ? 1
                                : null,
                    }
                )
                .pipe(takeUntil(this.destroy))
                .subscribe((res) => {
                    this.saving = false;
                    this.bottomSheetRef.dismiss(res);
                });
        } else {
            this.bottomSheetRef.dismiss();
        }
    }

    public ngOnDestroy() {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }
}
